import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ref, query, orderByChild, equalTo, get } from 'firebase/database';
import { db } from '../lib/firebase';
import { toast } from 'sonner';
import { User } from '../types/user';
import { PublicProfileView } from '../components/PublicProfileView';
import { useProfile } from '../hooks/useProfile';
import Loader from '../components/Loader';
import { logger } from '../lib/logger';

interface PublicProfileProps {
  preview?: boolean;
}

export default function PublicProfile({ preview }: PublicProfileProps): JSX.Element {
  const { username } = useParams<{ username: string }>();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { profile: currentProfile } = useProfile();

  useEffect(() => {
    const fetchProfile = async () => {
      // En mode preview, utiliser directement le profil courant
      if (preview) {
        setProfile(currentProfile);
        setLoading(false);
        return;
      }

      if (!username) {
        toast.error('Nom d\'utilisateur non spécifié');
        navigate('/');
        return;
      }

      try {
        // Chercher le profil par username
        const profilesRef = ref(db, 'vcards');
        const profileQuery = query(
          profilesRef,
          orderByChild('username'),
          equalTo(username)
        );

        const snapshot = await get(profileQuery);
        
        if (snapshot.exists()) {
          // Filtrer pour ne garder que les profils publics
          const profiles = Object.entries(snapshot.val())
            .filter(([_, data]) => (data as any).isPublic === true);

          if (profiles.length > 0) {
            const [uid, userData] = profiles[0];
            setProfile({
              ...userData as User,
              id: uid
            });
          } else {
            toast.error('Ce profil n\'est pas public');
            navigate('/');
          }
        } else {
          toast.error('Profil non trouvé');
          navigate('/');
        }
      } catch (error) {
        const errorMessage = logger.error('database', 'Erreur lors de la récupération du profil public', error);
        setError(errorMessage);
        toast.error('Erreur lors du chargement du profil');
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [username, navigate, preview, currentProfile]);

  if (loading) {
    return <Loader />;
  }

  if (!profile) {
    return null;
  }

  return <PublicProfileView profile={profile} />;
}
